import React, { useEffect, useRef, useState } from 'react';
import { Download, Image as Icone, Loader2, RefreshCw } from 'lucide-react';

import { assistantsService, type ConsigneVisuelle, type TextesAffiche } from '../../services/api/assistants';

interface Props {
  consigne: ConsigneVisuelle;
  conversationId: number;
  messageId: number;
}

const PROPORTIONS: Record<string, string> = {
  portrait: '3 / 4',
  paysage: '4 / 3',
  carré: '1 / 1',
  carre: '1 / 1',
};

/**
 * Visuel de communication : la consigne, puis l'image qu'elle produit.
 *
 * L'image n'est pas fabriquée avec le reste de la réponse : elle coûte, et une
 * consigne se relit avant d'être exécutée. On la demande donc explicitement,
 * et on peut la refaire sans repasser par l'assistant.
 *
 * La consigne reste modifiable : c'est en la retouchant qu'on rapproche
 * l'image de ce qu'on avait en tête, bien plus vite qu'en reformulant sa
 * demande à l'assistant.
 */
// Gabarits relevés sur les vraies affiches du PEV et de ses voisins ;
// « affiche-complete » est composée en entier par le modèle, dans ce style.
const GABARITS = ['affiche-complete', 'medaillon', 'photo-en-coin', 'bandeau-titre'];

const AFFICHE_VIDE: TextesAffiche = {
  titre: '', mot_fort: '', etiquette: '', dates: { du: '', au: '', mois: '' },
  cible: '', appel_action: '', pastille: "C'est gratuit", accroche: '',
};

const RenduVisuel: React.FC<Props> = ({ consigne, conversationId, messageId }) => {
  const estAffiche = consigne.support === 'affiche de sensibilisation' || !!consigne.affiche?.mot_fort;
  const [textes, setTextes] = useState<TextesAffiche>({ ...AFFICHE_VIDE, ...(consigne.affiche || {}) });
  const [invite, setInvite] = useState(consigne.invite);
  const [legende, setLegende] = useState(consigne.legende || '');
  const [format, setFormat] = useState(consigne.format_image || (estAffiche ? 'portrait' : 'paysage'));
  const [composition, setComposition] = useState(estAffiche ? 'affiche-complete' : 'bandeau-bas');
  const [aVerifier, setAVerifier] = useState<string[]>([]);
  const gabarit = GABARITS.includes(composition);
  const [image, setImage] = useState<string | null>(null);
  const [travail, setTravail] = useState(false);
  const [erreur, setErreur] = useState('');
  const precedente = useRef<string | null>(null);

  useEffect(
    () => () => {
      if (precedente.current) URL.revokeObjectURL(precedente.current);
    },
    [],
  );

  /**
   * Retrouve le dernier visuel produit, s'il y en a un.
   *
   * L'image ne vivait que dans cet écran : en revenant sur la conversation,
   * l'agent ne retrouvait plus son affiche et devait la refaire, donc la
   * repayer. Le serveur la conserve désormais ; on la redemande ici, sans
   * jamais en produire une nouvelle.
   */
  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const blob = await assistantsService.visuelConserve(conversationId, messageId);
        if (annule) return;
        if (precedente.current) URL.revokeObjectURL(precedente.current);
        precedente.current = URL.createObjectURL(blob);
        setImage(precedente.current);
      } catch {
        // Aucun visuel conservé : l'écran propose simplement de le produire.
      }
    })();
    return () => { annule = true; };
  }, [conversationId, messageId]);

  const produire = async () => {
    setTravail(true);
    setErreur('');
    setAVerifier([]);
    try {
      let blob: Blob;
      if (composition === 'affiche-complete') {
        const rendu = await assistantsService.genererAfficheComplete(
          conversationId, messageId, invite.trim(), format, textes,
        );
        blob = rendu.image;
        setAVerifier(rendu.aVerifier);
      } else blob = await assistantsService.genererVisuel(
        conversationId, messageId, invite.trim(), format, legende.trim(), composition,
        gabarit ? textes : undefined,
      );
      if (precedente.current) URL.revokeObjectURL(precedente.current);
      precedente.current = URL.createObjectURL(blob);
      setImage(precedente.current);
    } catch (e: any) {
      setErreur(e.message || "Le visuel n'a pas pu être produit.");
    } finally {
      setTravail(false);
    }
  };

  const telecharger = () => {
    if (!image) return;
    const lien = document.createElement('a');
    lien.href = image;
    lien.download = `${consigne.titre.replace(/[\\/:*?"<>|]/g, '')}.png`;
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
  };

  const retouchee = invite.trim() !== consigne.invite.trim();

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2 text-sm text-eg-gris">
          <Icone className="h-4 w-4" />
          {consigne.support}
        </div>
        <h2 className="mt-1 text-xl font-semibold text-eg-encre">{consigne.titre}</h2>
        {consigne.intention && (
          <p className="mt-1 text-[13px] text-eg-gris">{consigne.intention}</p>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {/* La consigne, relisible et retouchable avant exécution. */}
        <div className="space-y-2">
          <label className="block text-[10px] uppercase tracking-[0.04em] text-eg-gris">
            Consigne donnée au modèle d'images
          </label>
          <textarea
            value={invite}
            onChange={(e) => setInvite(e.target.value)}
            rows={9}
            className="w-full resize-y rounded-md border border-[#DDE2DD] bg-eg-blanc px-3 py-2
              text-[12.5px] leading-relaxed focus:border-eg-accent focus:outline-none"
          />

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              disabled={travail}
              className="rounded-md border border-[#DDE2DD] bg-eg-blanc px-2 py-1.5 text-[12.5px]
                focus:border-eg-accent focus:outline-none disabled:opacity-50"
            >
              <option value="portrait">Portrait — affiche</option>
              <option value="paysage">Paysage — rapport</option>
              <option value="carré">Carré — réseaux sociaux</option>
            </select>

            {/* La mise en page était la même pour tout : image en haut, texte
                en bas. On refaisait la consigne en croyant qu'elle n'était pas
                suivie, alors que c'est la composition qui ne bougeait pas. */}
            <select
              value={composition}
              onChange={(e) => setComposition(e.target.value)}
              disabled={travail}
              className="rounded-md border border-[#DDE2DD] bg-eg-blanc px-2 py-1.5 text-[12.5px]
                focus:border-eg-accent focus:outline-none disabled:opacity-50"
            >
              <optgroup label="Affiche">
                <option value="affiche-complete">Affiche complète, style PEV (composée par le modèle)</option>
                <option value="medaillon">Affiche : photo en médaillon</option>
                <option value="photo-en-coin">Affiche : photo en coin, fond jaune</option>
                <option value="bandeau-titre">Affiche : titre en tête, photo large</option>
              </optgroup>
              <optgroup label="Visuel légendé">
                <option value="bandeau-bas">Texte sous l'image</option>
                <option value="bandeau-haut">Texte au-dessus de l'image</option>
                <option value="texte-sur-image">Texte posé sur l'image</option>
              </optgroup>
            </select>

            <button
              type="button"
              onClick={produire}
              disabled={travail || !invite.trim()}
              className="inline-flex items-center gap-1.5 rounded-[5px] bg-eg-ombre px-3.5 py-1.5
                text-[12.5px] font-semibold text-eg-blanc transition-colors hover:bg-eg-encre
                disabled:opacity-40"
            >
              {travail ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              {image ? 'Refaire le visuel' : 'Produire le visuel'}
            </button>

          </div>

          {retouchee && (
            <p className="text-[11px] text-eg-alerte">
              Consigne retouchée — le prochain visuel suivra votre version.
            </p>
          )}
        </div>

        {/* L'image produite, aux proportions demandées. */}
        <div
          className="flex items-center justify-center overflow-hidden rounded-md border
            border-[#DDE2DD] bg-eg-papier"
          style={{ aspectRatio: PROPORTIONS[format] || '4 / 3' }}
        >
          {image ? (
            <img src={image} alt={consigne.intention || consigne.titre}
              className="h-full w-full object-contain" />
          ) : (
            <div className="px-6 text-center text-[12px] text-eg-estompe">
              {travail ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> production en cours…
                </span>
              ) : (
                'Relisez la consigne, puis produisez le visuel.'
              )}
            </div>
          )}
        </div>

        {/* Le téléchargement était rangé dans la barre d'outils, au-dessus du
            visuel, sous le seul libellé « .png ». Les agents d'EG ne l'y
            trouvaient pas : on cherche à enregistrer une image là où on la
            regarde, pas dans les réglages qui ont servi à la produire. */}
        {image && (
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={telecharger}
              className="inline-flex items-center gap-1.5 rounded-[5px] bg-eg-ombre px-3.5 py-1.5
                text-[12.5px] font-semibold text-eg-blanc transition-colors hover:bg-eg-encre"
            >
              <Download className="h-3.5 w-3.5" /> Télécharger le visuel (.png)
            </button>
          </div>
        )}
      </div>

      {aVerifier.length > 0 && (
        <div className="rounded-xl bg-[#FFF6E0] px-4 py-3 text-[13px] text-[#7A5200]">
          <p className="font-medium">À relire avant impression : ces textes n'ont pas été retrouvés tels quels sur l'affiche.</p>
          <ul className="mt-1 list-disc pl-5">
            {aVerifier.map((t) => <li key={t}>{t}</li>)}
          </ul>
          <p className="mt-1.5 text-[12px]">Refaites l'affiche, ou choisissez un gabarit : le texte y est posé sans faute.</p>
        </div>
      )}

      {erreur && (
        <p className="rounded-md border border-eg-accent/30 bg-eg-accent/10 px-3 py-2 text-sm
          text-eg-accent">{erreur}</p>
      )}

      {/* La légende n'est pas demandée au modèle d'images — il écrit mal, et
          plus mal encore le français accentué. Elle est posée après coup, avec
          une vraie police, sur un bandeau sous le visuel. Elle se retouche donc
          ici, et l'affiche sort finie. */}
      {gabarit ? (
        <div className="rounded-2xl bg-eg-papier px-4 py-3">
          <div className="mb-2 text-[10px] uppercase tracking-[0.04em] text-eg-gris">
            Textes de l'affiche : posés par la plateforme, sans faute
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {([
              ['titre', 'Titre'],
              ['mot_fort', 'Mot en très gros (maladie, vaccin)'],
              ['etiquette', 'Étiquette (tour, période)'],
              ['pastille', 'Pastille'],
              ['cible', 'Cible'],
              ['appel_action', "Appel à l'action"],
              ['accroche', 'Accroche'],
            ] as Array<[Exclude<keyof TextesAffiche, 'dates'>, string]>).map(([cle, libelle]) => (
              <label key={cle} className={`block text-[11px] text-eg-gris ${cle === 'cible' || cle === 'appel_action' || cle === 'accroche' ? 'sm:col-span-2' : ''}`}>
                {libelle}
                <input
                  value={textes[cle]}
                  onChange={(e) => setTextes({ ...textes, [cle]: e.target.value })}
                  className="mt-0.5 w-full rounded-lg bg-eg-blanc px-2.5 py-1.5 text-[13px] text-eg-encre
                    outline outline-1 -outline-offset-1 outline-black/[0.08] focus:outline-eg-accent"
                />
              </label>
            ))}
            <div className="grid grid-cols-3 gap-2 sm:col-span-2">
              {(['du', 'au', 'mois'] as const).map((cle) => (
                <label key={cle} className="block text-[11px] text-eg-gris">
                  {cle === 'du' ? 'Du (jour)' : cle === 'au' ? 'Au (jour)' : 'Mois et année'}
                  <input
                    value={textes.dates[cle]}
                    onChange={(e) => setTextes({ ...textes, dates: { ...textes.dates, [cle]: e.target.value } })}
                    className="mt-0.5 w-full rounded-lg bg-eg-blanc px-2.5 py-1.5 text-[13px] text-eg-encre
                      outline outline-1 -outline-offset-1 outline-black/[0.08] focus:outline-eg-accent"
                  />
                </label>
              ))}
            </div>
          </div>
          <p className="mt-2 text-[11px] text-eg-estompe">
            Un champ laissé vide disparaît de l'affiche. Armoiries, PEV et Gavi sont posés en pied.
            {composition === 'affiche-complete' && " Le modèle écrit lui-même ces textes : l'affiche est relue et tout écart est signalé."}
          </p>
        </div>
      ) : (
      <div className="rounded-md border border-eg-bord bg-eg-papier px-4 py-3">
        <label className="mb-1 block text-[10px] uppercase tracking-[0.04em] text-eg-gris">
          Légende — posée sur l'affiche, sous le visuel
        </label>
        <textarea
          value={legende}
          onChange={(e) => setLegende(e.target.value)}
          rows={2}
          className="w-full resize-y rounded-md border border-[#DDE2DD] bg-eg-blanc px-3 py-2
            text-[13px] leading-relaxed focus:border-eg-accent focus:outline-none"
        />
        {consigne.mentions_obligatoires?.length > 0 && (
          <ul className="mt-2 space-y-0.5 border-t border-eg-bordure pt-2 text-[12px] text-eg-gris">
            {consigne.mentions_obligatoires.map((mention, rang) => (
              <li key={rang}>{mention}</li>
            ))}
          </ul>
        )}
        <p className="mt-2 text-[11px] text-eg-estompe">
          Les armoiries et le logo du programme sont ajoutés en pied d'affiche.
        </p>
      </div>
      )}
    </div>
  );
};

export default RenduVisuel;
